/* ============================================
   TTS Stream Client — Browser WebSocket client
   ============================================
   Connects to /ws/tts-stream on the server.
   Sends text phrases, receives audio chunks.
   Used by CommentaryHybridEngine for continuous
   template playback without per-phrase HTTP.
   ============================================ */

export class TTSStreamClient {
  /**
   * @param {object} options
   * @param {string}   options.lang          - Language code (e.g. 'fr', 'en')
   * @param {function} options.onAudioChunk  - (base64Chunk, id) => void
   * @param {function} options.onDone        - (id) => void — called when a speak() finishes
   * @param {function} options.onError       - (message) => void
   * @param {function} options.onLog         - (message) => void
   * @param {function} options.onReady       - () => void — called when session is ready
   */
  constructor(options = {}) {
    this.lang = options.lang || 'en'
    this.onAudioChunk = options.onAudioChunk || (() => {})
    this.onDone = options.onDone || (() => {})
    this.onError = options.onError || (() => {})
    this.onLog = options.onLog || ((msg) => console.log(msg))
    this.onReady = options.onReady || (() => {})

    this._ws = null
    this._ready = false
    this._readyPromise = null
    this._readyResolve = null
    this._reconnecting = false
    this._closed = false
  }

  /**
   * Connect to the server WebSocket and initialize the TTS session.
   * Resolves when the server sends { type: "ready" }.
   */
  async connect() {
    if (this._ws && this._ws.readyState === WebSocket.OPEN) {
      return true
    }

    this._closed = false
    this._ready = false

    return new Promise((resolve, reject) => {
      const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
      const url = `${protocol}//${location.host}/ws/tts-stream`

      this.onLog(`[TTS-WS] Connecting to ${url}`)
      this._ws = new WebSocket(url)

      this._readyPromise = new Promise((res) => { this._readyResolve = res })

      this._ws.onopen = () => {
        this.onLog('[TTS-WS] Connected, sending init')
        this._ws.send(JSON.stringify({
          type: 'init',
          lang: this.lang,
        }))
      }

      this._ws.onmessage = (event) => {
        let msg
        try {
          msg = JSON.parse(event.data)
        } catch (e) {
          return
        }

        switch (msg.type) {
          case 'ready':
            this._ready = true
            if (this._readyResolve) {
              this._readyResolve()
              this._readyResolve = null
            }
            this.onLog(`[TTS-WS] Session ready${msg.mock ? ' (mock mode)' : ''}`)
            this.onReady()
            resolve(true)
            break

          case 'audio':
            this.onAudioChunk(msg.chunk, msg.id)
            break

          case 'audio_done':
            this.onDone(msg.id)
            break

          case 'error':
            this.onError(msg.message)
            this.onLog(`[TTS-WS] Server error: ${msg.message}`)
            break
        }
      }

      this._ws.onerror = (err) => {
        this.onLog(`[TTS-WS] WebSocket error`)
        this.onError('WebSocket connection error')
        reject(new Error('WebSocket connection error'))
      }

      this._ws.onclose = (event) => {
        this._ready = false
        this.onLog(`[TTS-WS] Disconnected (code=${event.code})`)
        if (!this._closed) {
          this._scheduleReconnect()
        }
      }

      // Timeout for initial connection
      setTimeout(() => {
        if (!this._ready) {
          this.onLog('[TTS-WS] Connection timeout')
          reject(new Error('Connection timeout'))
        }
      }, 10000)
    })
  }

  /**
   * Send a text to speak.
   * @param {string} id   - Unique identifier for tracking
   * @param {string} text - Text to synthesize
   */
  speak(id, text) {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN || !this._ready) {
      this.onLog(`[TTS-WS] Cannot speak — not connected`)
      return false
    }

    this._ws.send(JSON.stringify({
      type: 'speak',
      id,
      text,
    }))
    return true
  }

  /**
   * Interrupt the current audio generation.
   */
  interrupt() {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return
    this._ws.send(JSON.stringify({ type: 'interrupt' }))
    this.onLog('[TTS-WS] Interrupt sent')
  }

  /**
   * Close the session and disconnect.
   */
  close() {
    this._closed = true
    this._ready = false
    if (this._ws) {
      if (this._ws.readyState === WebSocket.OPEN) {
        try {
          this._ws.send(JSON.stringify({ type: 'close' }))
        } catch (e) { /* ignore */ }
      }
      this._ws.close()
      this._ws = null
    }
    this.onLog('[TTS-WS] Closed')
  }

  /**
   * Whether the client is connected and ready to speak.
   */
  get isReady() {
    return this._ready && this._ws && this._ws.readyState === WebSocket.OPEN
  }

  /**
   * Wait until the session is ready (for use after connect()).
   */
  async waitReady() {
    if (this._ready) return
    if (this._readyPromise) await this._readyPromise
  }

  _scheduleReconnect() {
    if (this._reconnecting || this._closed) return
    this._reconnecting = true
    this.onLog('[TTS-WS] Will reconnect in 3s')
    setTimeout(async () => {
      this._reconnecting = false
      if (this._closed) return
      try {
        await this.connect()
      } catch (e) {
        this.onLog(`[TTS-WS] Reconnect failed: ${e.message}`)
      }
    }, 3000)
  }
}
